import React, { createContext, useState, useContext, useEffect } from 'react';
import PartnersServices from '../api/services/partners';
import {useSelector} from "react-redux";

const PartnerContext = createContext();

export const PartnerProvider = ({ children }) => {
    const [currentPartnerId, setCurrentPartnerId] = useState(() => localStorage.getItem('currentPartnerId') || null);
    const [allPartners, setAllPartners] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const {user, token} = useSelector(state => state?.auth || { user: null, token: null });

    // Routes publiques qui n'ont pas besoin du contexte partner
    const isPublicRoute = () => {
        const currentPath = window.location.pathname;
        const publicRoutes = [
            '/activities/', // ActivityPreview
            '/partner-public/', // PartnerPublicView
            '/privacy-policy',
            '/legals-mentions', 
            '/usage-policy',
            '/faq',
            '/subscription-policy',
            '/login',
            '/signin',
            '/password-reset',
            '/generate-proof-of-booking-user/',
            '/stripe-payement',
            '/booking-stripe'
        ];
        
        return publicRoutes.some(route => currentPath.startsWith(route));
    };

    // Fetch all partners for super-admin or multi-partner users
    const fetchAllPartners = async () => {
        try {
            setError(null);
            setIsLoading(true);

            // Vérification de l'authentification
            if (!user || !token) {
                setError("Session expirée ou non authentifié");
                return;
            }

            const response = await PartnersServices.getAllPartners();
            if (response.status === 200) {
                setAllPartners(response.data);
            }
        } catch (error) {
            console.error("Error fetching partners:", error);
            if (error.status === 403) {
                setError("Accès non autorisé. Veuillez vous reconnecter.");
            } else if (error.status === 401) {
                setError("Session expirée. Veuillez vous reconnecter.");
            } else {
                setError("Une erreur est survenue lors de la récupération des partenaires.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Set a partner ID
    const setPartnerId = (partnerId) => {
        if (partnerId) {
            localStorage.setItem('currentPartnerId', partnerId);
            setCurrentPartnerId(partnerId);
        }
    };

    // Clear partner ID
    const clearPartnerId = () => {
        localStorage.removeItem('currentPartnerId');
        setCurrentPartnerId(null);
    };

    const callAllPartners = async () => {
        try {
            const response = await PartnersServices.getAllPartners();
            if (response.status === 200) {
                setAllPartners(response.data);
            }
        } catch (error) {
            console.error("Error fetching partners:", error);
        }
    };

    // Nouvelle fonction pour mettre à jour un partenaire spécifique
    const updatePartnerData = (partnerId, updateData) => {
        setAllPartners(prevPartners => 
            prevPartners.map(partner => 
                partner._id === partnerId 
                    ? { ...partner, ...updateData } 
                    : partner
            )
        );
    };

    // Fonction optimisée pour mettre à jour un compte Stripe connecté
    const updatePartnerStripeAccount = (partnerId, stripeData) => {
        setAllPartners(prevPartners => 
            prevPartners.map(partner => 
                partner._id === partnerId 
                    ? { 
                        ...partner, 
                        subscriptionID: {
                            ...partner.subscriptionID,
                            ...stripeData
                        }
                      } 
                    : partner
            )
        );
    };

    useEffect(() => {
        // Ne charger les partenaires que si ce n'est pas une route publique
        if (user && token && !isPublicRoute()) {
            fetchAllPartners();
        }
    }, [currentPartnerId, user, token]);

    // Écouter les changements de route pour réagir aux navigations
    useEffect(() => {
        const handleLocationChange = () => {
            // Re-vérifier si on doit charger les partenaires après un changement de route
            if (user && token && !isPublicRoute()) {
                fetchAllPartners();
            }
        };

        // Écouter les changements d'URL (pour SPA)
        window.addEventListener('popstate', handleLocationChange);
        
        return () => {
            window.removeEventListener('popstate', handleLocationChange);
        };
    }, [user, token]);

    return (
        <PartnerContext.Provider value={{ 
            currentPartnerId, 
            setPartnerId, 
            clearPartnerId, 
            allPartners, 
            callAllPartners,
            updatePartnerData,
            updatePartnerStripeAccount,
            isLoading,
            error
        }}>
            {children}
        </PartnerContext.Provider>
    );
};

export const usePartner = () => {
    const context = useContext(PartnerContext);
    if (context === undefined) {
        throw new Error('usePartner doit être utilisé à l\'intérieur d\'un PartnerProvider');
    }
    return context;
};

